import { Badge, Button, EmptyState, Input, Select, Table, type TableColumn } from '@bennows/boows-react';
import { ArrowUpDown, Download, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { formatDate, formatDateTime, formatNumber } from '../../lib/format';
import { useToast } from '../../lib/toasts';
import { BILLING_TONES } from './billingTone';
import {
  BILLING_STATES,
  INDUSTRY_PROFILES,
  TENANT_SORTS,
  useTenantExport,
  useTenants,
  type BillingState,
  type IndustryProfile,
  type TenantFilter,
  type TenantSort,
  type TenantSummary,
} from './useTenants';

/** Below this width the list shows cards (as the operators list). */
const CARD_BREAKPOINT = 640;
const SEARCH_DELAY_MS = 300;
/** Select option for "no filter"; the kit's Select treats an empty id as no choice. */
const ALL = 'all';
const NO_FILTER: TenantFilter = { q: '', industryProfile: '', billingState: '', sort: 'name' };

/** All tenants with billing state, use and last activity, numbers only (REQ-140-011, REQ-OPS-017). */
export function TenantsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [filter, setFilter] = useState<TenantFilter>(NO_FILTER);
  const [search, setSearch] = useState('');
  const tenants = useTenants(filter);
  const exporter = useTenantExport();
  const rows = tenants.data?.pages.flatMap((page) => page.data) ?? [];
  const filtered = filter.q !== '' || filter.industryProfile !== '' || filter.billingState !== '';

  useEffect(() => {
    const timer = setTimeout(() => setFilter((current) => (current.q === search ? current : { ...current, q: search })), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const reset = () => {
    setSearch('');
    setFilter((current) => ({ ...NO_FILTER, sort: current.sort }));
  };

  const download = () =>
    exporter.mutate(
      { filter, fileName: t('tenants.exportFile', { date: new Date().toISOString().slice(0, 10) }) },
      { onSuccess: () => toast(t('tenants.exported')), onError: () => toast(t('tenants.exportFailed'), 'error') },
    );

  const count = (value: number | null) => (value === null ? t('tenants.notYet') : formatNumber(value));
  const columns: TableColumn<TenantSummary>[] = [
    {
      key: 'company',
      header: t('tenants.columns.company'),
      cell: (row) => (
        <span className="ui-console-stacked">
          <Link className="ui-console-name__link" to={`/tenants/${row.id}`}>
            {row.name}
          </Link>
          <span className="ui-console-muted">{t('tenants.companyLine', { slug: row.slug, industry: t(`industryProfiles.${row.industry_profile}`) })}</span>
        </span>
      ),
    },
    {
      key: 'contact',
      header: t('tenants.columns.contact'),
      cell: (row) =>
        row.admin_contact ? (
          <span className="ui-console-stacked">
            {row.admin_contact.name}
            <span className="ui-console-muted">{row.admin_contact.email}</span>
          </span>
        ) : (
          <span className="ui-console-muted">{t('tenants.noContact')}</span>
        ),
    },
    {
      key: 'billing',
      header: t('tenants.columns.billing'),
      cell: (row) => (
        <span className="ui-console-stacked">
          <span>
            <Badge tone={BILLING_TONES[row.billing_state]}>{t(`billingStates.${row.billing_state}`)}</Badge>
          </span>
          {row.balance !== null ? <span className="ui-console-muted">{t('tenants.balance', { count: formatNumber(row.balance) })}</span> : null}
        </span>
      ),
    },
    { key: 'credits', header: t('tenants.columns.credits'), align: 'right', cell: (row) => count(row.credits_month) },
    {
      key: 'posts',
      header: t('tenants.columns.posts'),
      align: 'right',
      cell: (row) =>
        row.posts_created_month === null ? (
          t('tenants.notYet')
        ) : (
          <span className="ui-console-stacked">
            {formatNumber(row.posts_created_month)}
            <span className="ui-console-muted">{t('tenants.publishedLine', { count: row.published_month ?? 0 })}</span>
          </span>
        ),
    },
    {
      key: 'lastActivity',
      header: t('tenants.columns.lastActivity'),
      cell: (row) => (row.last_activity_at ? formatDateTime(row.last_activity_at) : t(row.metrics_at ? 'tenants.never' : 'tenants.notYet')),
    },
    { key: 'created', header: t('tenants.columns.created'), cell: (row) => formatDate(row.created_at) },
  ];

  return (
    <div className="ui-stack">
      <PageHeader
        title={t('tenants.title')}
        description={t('tenants.description')}
        actions={
          <Button variant="secondary" iconPrefix={<Download aria-hidden="true" />} loading={exporter.isPending} onClick={download}>
            {t('tenants.export')}
          </Button>
        }
      />
      <div className="ui-console-filters">
        <Input
          type="search"
          hideLabel
          label={t('tenants.search')}
          prefix={<Search aria-hidden="true" size={16} />}
          placeholder={t('tenants.searchPlaceholder')} value={search}
          onChange={setSearch}
        />
        <Select
          hideLabel
          label={t('tenants.industry')}
          value={filter.industryProfile || ALL}
          options={[{ id: ALL, label: t('tenants.allIndustries') }, ...INDUSTRY_PROFILES.map((id) => ({ id, label: t(`industryProfiles.${id}`) }))]}
          onChange={(id) => setFilter((current) => ({ ...current, industryProfile: id === ALL ? '' : (id as IndustryProfile) }))}
        />
        <Select
          hideLabel
          label={t('tenants.billingState')}
          value={filter.billingState || ALL}
          options={[{ id: ALL, label: t('tenants.allBillingStates') }, ...BILLING_STATES.map((id) => ({ id, label: t(`billingStates.${id}`) }))]}
          onChange={(id) => setFilter((current) => ({ ...current, billingState: id === ALL ? '' : (id as BillingState) }))}
        />
        <Select
          hideLabel
          label={t('tenants.sort')}
          value={filter.sort}
          options={TENANT_SORTS.map((id) => ({ id, label: t(`tenants.sorts.${id}`), prefix: <ArrowUpDown aria-hidden="true" size={16} /> }))}
          onChange={(id) => setFilter((current) => ({ ...current, sort: id as TenantSort }))}
        />
      </div>
      {tenants.isError ? (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t('tenants.loadError')}</p>
          <Button variant="secondary" onClick={() => void tenants.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <>
          <Table
            ariaLabel={t('tenants.listLabel')}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={tenants.isPending}
            emptyState={
              filtered ? (
                <EmptyState
                  title={t('tenants.emptyFiltered')}
                  action={
                    <Button variant="secondary" onClick={reset}>
                      {t('tenants.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState title={t('tenants.empty')} />
              )
            }
            labels={{ empty: t(filtered ? 'tenants.emptyFiltered' : 'tenants.empty') }}
            cardBreakpoint={CARD_BREAKPOINT}
            viewToggle={false}
            mobileViewToggle={false}
            renderCard={(row) => (
              <div className="ui-console-card">
                <span className="ui-console-name">
                  <Link className="ui-console-name__link" to={`/tenants/${row.id}`}>
                    {row.name}
                  </Link>
                  <Badge tone={BILLING_TONES[row.billing_state]}>{t(`billingStates.${row.billing_state}`)}</Badge>
                </span>
                <span className="ui-console-muted">{row.admin_contact?.email ?? t('tenants.noContact')}</span>
                {row.credits_month !== null ? <span>{t('tenants.card.credits', { count: row.credits_month })}</span> : null}
                {row.posts_created_month !== null ? <span>{t('tenants.card.posts', { count: row.posts_created_month })}</span> : null}
                {row.last_activity_at ? <span className="ui-console-muted">{t('tenants.card.lastActivity', { time: formatDateTime(row.last_activity_at) })}</span> : null}
              </div>
            )}
          />
          {rows.length > 0 ? (
            <div className="ui-console-more">
              <span className="ui-console-muted" aria-live="polite">
                {t('tenants.count', { count: rows.length })}
              </span>
              {tenants.hasNextPage ? (
                <Button variant="secondary" loading={tenants.isFetchingNextPage} onClick={() => void tenants.fetchNextPage()}>
                  {t('tenants.more')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
